import * as angular from 'angular';
import * as moment from 'moment';

import { ComponentDefinition, IDateRangePickerOptions, TimeUnit } from '../../../models';
import { IOnlineComputationService, IDateRangeOptionsBuilder, ICurrentApplicationService } from '../../../services';
import { momentFormat, jobStarting, jobStarted, jobStartFailed } from '../../../common';
import { toDuration } from '../../../services/helpers/toDuration';
import { getTimeZone } from '../../../common/getTimezone';

class StartJobModalController implements angular.IComponentController {
    datePicker: { date: moment.Moment };
    firstNonProcessed: string;
    group: any;
    isLoading: boolean;
    lastProcessedChecked: boolean;
    loadingMessage: string;
    mode: string = 'startDate';
    nowChecked: boolean;
    options: IDateRangePickerOptions;
    startDateChecked: boolean;

    readonly format: string;

    static $inject = [
        '$scope',
        '$rootScope',
        '$timeout',
        'currentApplicationService',
        'dateRangeOptionsBuilder',
        'onlineComputationService'
    ];
    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _currentApplication: ICurrentApplicationService,
        private _dateRangeOptionsBuilder: IDateRangeOptionsBuilder,
        private _onlineComputationService: IOnlineComputationService,
    ) {
    }

    $onInit() {
        this.refreshDate();
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['group'] && changesObj['group'].currentValue) {
            this.refreshDate();
        }
    }

    private refreshDate() {
        this.options = this.setOptions();
        this.resetSelection();
        const beginDayTime = this._currentApplication.getApplicationConfiguration().beginDayTime;
        let startTime = moment().startOf('d').add(beginDayTime);
        if (startTime.isAfter(moment())) {
            startTime = startTime.subtract(1, 'd');
        }
        this.datePicker = { date: startTime };

        if (this.group) {
            this.firstNonProcessed = this.getNextToProcess(this.group.lastProcessed);
            this._$timeout(() => {
                const rangePicker = $('#startjob-datepicker').data('daterangepicker');
                if (rangePicker) {
                    rangePicker.setStartDate(this.datePicker.date);
                    rangePicker.setEndDate(this.datePicker.date);
                }
            });
        }
    }

    private getNextToProcess(date: string): string {
        const freq = this.group?.frequency
            ? toDuration(this.group.frequency)
            : toDuration(this._currentApplication.getApplicationConfiguration());

        return moment(date).add(freq).toISOString();
    }

    private setOptions() {
        const self = this;

        const config = this._currentApplication.getApplicationConfiguration();

        // Set time picker interval in minutes
        let timeIncrement: number = 1;
        timeIncrement = (config.intervalTimeUnit === TimeUnit.Day) ? config.interval * 24 * 60 : config.interval;
        if (config.intervalTimeUnit === TimeUnit.Hour) {
            timeIncrement = 60;
        }
        if (config.intervalTimeUnit === TimeUnit.Minute) {
            timeIncrement = config.interval;
        }

        // Configure date range picker
        const options: IDateRangePickerOptions = {
            applyClass: 'btn-primary',
            autoUpdateInput: true,
            autoApply: true,
            timePicker24Hour: true,
            timePicker: true,
            timePickerIncrement: timeIncrement,
            singleDatePicker: true,
            showDropdowns: true,
            alwaysShowCalendars: true,
            locale: {
                format: this.format || momentFormat
            },
            eventHandlers: {
                'apply.daterangepicker': (evt, picker) => {
                    self.group.startDate = this.datePicker.date.format(momentFormat);
                    self._$scope.$apply();
                }
            }
        };
        return this._dateRangeOptionsBuilder.translate(options);
    }

    private setLastProcessed() {
        if (this.group) {
            //this.group.startDate = moment(this.group.lastProcessed).format(momentFormat);
            this.group.startDate = moment(this.firstNonProcessed).format(momentFormat);
        }
    }

    private setNow() {
        if (this.group) {
            this.group.startDate = moment().format(momentFormat);
        }
    }

    start() {
        if (this.mode === 'lastProcessed') {
            this.setLastProcessed();
        }
        else if (this.mode === 'now') {
            this.setNow();
        }
        else {
            this.group.startDate = this.datePicker.date;
            if (!this.group.startDate) {
                this.group.startDate = moment().startOf('d');
            }
        }
        const computationJobDefinition = {
            guid: this.group.jobGuid,
            groupGuid: this.group.guid,
            groupName: this.group.uniqueName,
            isChildrenIncluded: this.group.isChildrenSelected,
            reconcile: this.group.isReconciliationSelected,
            startDate: moment(this.group.startDate).toISOString(),
            olsonTimeZone: getTimeZone(),
            delay: this.group.delay
        };
        this._$rootScope.$broadcast(jobStarting);
        this._onlineComputationService
            .start(computationJobDefinition)
            .then(data => {
                this._$rootScope.$broadcast(jobStarted, data);
                this._$timeout(() => {
                    this.resetSelection();
                });
            })
            .catch(() => {
                this._$rootScope.$broadcast(jobStartFailed);
            });
    }

    cancel() {
        this.resetSelection();
    }

    onOpen() {
        this.resetSelection();
    }

    private resetSelection() {
        this.nowChecked = false;
        this.lastProcessedChecked = !!this.group && this.group.lastProcessed;
        this.startDateChecked = !this.lastProcessedChecked;
        this.mode = this.lastProcessedChecked
            ? 'lastProcessed'
            : 'startDate';
    }
}

export const wtStartJobModal: ComponentDefinition = {
    componentId: 'wtStartJobModal',
    controller: StartJobModalController,
    template: require('./startJobModal.component.html'),
    controllerAs: 'vm',
    bindings: {
        format: '<',
        group: '<'
    }
};